import { ref } from 'vue';
import { defineStore } from 'pinia';
import { associateBy } from '@std/collections';

import {
  getGlossaryData,
  getGlossaryVersion,
  type GlossaryResponse,
} from '@/composables/network-manager';
import type { ApplicationStore } from './types';
import type { Nation, Vehicle, VehicleType } from '@/types/gloss';

import { useDogtagStore } from '@/models/dogtag-store';

const ID = 'mk-replay-app-store';
const isInit = ref(false);

/**
 * Load application store, try to load from localstorage when glossary version match
 */
export const useApplicationStore = defineStore(ID, () => {
  const store = ref<ApplicationStore>({
    dialogs: {
      deleteReplay: {
        isRequest: false,
        isShow: false,
        replayData: undefined,
        initiator: null,
      },
    },
  });

  const saveToLocalStorage = (data: GlossaryResponse | null) => {
    try {
      localStorage.setItem(ID, JSON.stringify(data));
    } catch (e) {
      console.error('cannot save local storage', e);
    }
  };

  const loadDataToStore = (data: GlossaryResponse) => {
    store.value.nation = associateBy(data.data.nations as Nation[], (i) => i.name!);
    store.value.vehicleType = associateBy(data.data.vehicleTypes as VehicleType[], (i) => i.name!);
    store.value.vehicle = associateBy(data.data.vehicles as Vehicle[], (i) => i.id!);

    const { fillData: fillDogtagData } = useDogtagStore();
    fillDogtagData(data);

    isInit.value = true;
    saveToLocalStorage(data);
  };

  const loadFromLocalStorage = () => {
    let result: GlossaryResponse | undefined = undefined;
    try {
      const stringData = localStorage.getItem(ID);
      if (stringData) {
        result = JSON.parse(stringData) as GlossaryResponse;
      }
    } catch (e) {
      console.error('cannot load from local storage', e);
      return result;
    }

    return result;
  };

  const getCurrentVersion = async () => getGlossaryVersion();

  const fillData = async () => {
    const activeVersion = await getCurrentVersion();
    const storageData = loadFromLocalStorage();
    const storageVersion = storageData?.data?.version;

    if (storageData && activeVersion === storageVersion) {
      loadDataToStore(storageData);
      return;
    }

    const { status, data, error } = await getGlossaryData();
    if (status.value === 'success' && data.value) {
      loadDataToStore(data.value);
    }

    if (error.value) {
      throw error;
    }

    watch(status, (newStatusValue) => {
      if (newStatusValue === 'success' && data.value) {
        loadDataToStore(data.value);
      }
    });
  };

  if (!isInit.value) {
    fillData();
  }

  const nation = computed(() => store.value.nation ?? {});
  const vehicle = computed(() => store.value.vehicle ?? {});
  const vehicleType = computed(() => store.value.vehicleType ?? {});

  return {
    nation,
    vehicle,
    vehicleType,
    state: store,
    isInit,
    dialogs: store.value.dialogs,
  };
});
